/**
 * Dietary split for included add-ons — how many special meals to order.
 *
 * One row per TICKET TYPE + add-on, and only for pairs where the organizer turned dietary
 * options on. An included tote bag or a beer has no dietary question, so it does not appear
 * here at all — not as a row, not in the totals. Attendance and vendor reconciliation are
 * separate concerns with their own numbers.
 *
 * The Total section rolls each add-on up across the tiers that ask, which is the number a
 * caterer is given when several ticket types share one meal.
 *
 * Renders nothing unless the backend returned rows — the dietary-enabled decision is made
 * there, once, rather than re-derived here.
 */
import React from "react";
import { useTranslation } from "react-i18next";

import TwProgressBar from "../../../components/tailwind/TwProgressBar";

export default function DietarySummaryCard({ dietarySummary }) {
  const { t } = useTranslation();

  const rows = Array.isArray(dietarySummary?.rows) ? dietarySummary.rows : [];
  const totals = Array.isArray(dietarySummary?.totals) ? dietarySummary.totals : [];
  if (rows.length === 0) return null;

  return (
    <div className="w-full mt-3 p-4 bg-white border-2 border-gray-200 rounded-2xl">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-semibold text-gray-900">
          {t("myEvents.dietarySplitTitle", "Dietary preferences")}
        </h2>
        <p className="text-xs text-gray-400">
          {t(
            "myEvents.dietarySplitSubtitle",
            "Confirmed attendees only. Cancelled bookings are excluded."
          )}
        </p>
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6">
        {rows.map((row, i) => {
          const total = parseInt(row?.totalAttendees || 0);
          return (
            <div key={`${row?.ticketType || ""}-${row?.addonName || ""}-${i}`} className="w-full">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-bold text-gray-800 line-clamp-1">
                  {row?.ticketType} — {row?.addonName}
                </span>
                <span className="text-sm text-gray-400 whitespace-nowrap">
                  {t("myEvents.dietarySplitAttendees", "{{total}} attendees", { total })}
                </span>
              </div>

              <div className="mt-2 space-y-2">
                {(row?.options || []).map((option, j) => {
                  const count = parseInt(option?.count || 0);
                  // Guard the divide: the backend drops zero-attendee rows, but a stale
                  // cached response should not render NaN%.
                  const percent = total > 0 ? parseFloat(((count / total) * 100).toFixed(1)) : 0;
                  return (
                    <div key={`${option?.option || "option"}-${j}`} className="flex flex-col w-full">
                      <div className="text-sm text-gray-800 flex gap-1">
                        <span className="line-clamp-1">{option?.option}</span>
                        <span className="text-gray-400">({percent}%)</span>
                      </div>
                      <div className="flex justify-between items-center gap-1">
                        <TwProgressBar value={percent} color={"bg-primary"} max={100} />
                        <div className="text-sm font-semibold text-gray-900 w-12 text-right">
                          {count}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {totals.length > 0 ? (
        <div className="mt-6 pt-3 border-t border-gray-200">
          <p className="text-sm font-semibold text-gray-900">
            {t("myEvents.dietaryTotal", "Total")}
          </p>

          <div className="mt-2 space-y-2">
            {totals.map((item, i) => (
              <div
                key={`${item?.addonName || "addon"}-${i}`}
                className="flex flex-wrap items-baseline gap-x-3 gap-y-1"
              >
                <span className="text-sm font-semibold text-gray-900 min-w-32">
                  {item?.addonName} — {parseInt(item?.totalAttendees || 0)}
                </span>
                <span className="text-sm text-gray-600">
                  {(item?.options || [])
                    .map((o) => `${o?.option} ${parseInt(o?.count || 0)}`)
                    .join(" · ")}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
